import React, { useState, useEffect } from "react";
import {
  Library,
  Search,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  X,
  Tag,
  Calendar,
  Hash,
  LayoutGrid,
  List,
  Copy,
  Check,
  Filter,
} from "lucide-react";
import { useBodyScrollLock } from "../utils/useBodyScrollLock";
import { kmsApi } from "../api/client";
import { fmtNumber, fmtDate, fmtRelative } from "../utils/format";

const VALIDATION_BADGES = {
  canonical: { cls: "badge-green", label: "Canonical" },
  verified: { cls: "badge-saffron", label: "Verified" },
  draft: { cls: "badge-neutral", label: "Draft" },
  processed: { cls: "badge-blue", label: "Processed" },
};

const DOMAINS = [
  "",
  "vaicharik",
  "sangathana",
  "seva",
  "shiksha",
  "samajik_samarasta",
  "rashtriya_suraksha",
  "sanskriti",
  "itihaas",
  "general",
];

const Skeleton = ({
  width = "100%",
  height = 20,
  borderRadius = 4,
  style = {},
}) => (
  <div
    className="skeleton-box"
    style={{
      display: "inline-block",
      width,
      height,
      borderRadius,
      ...style,
    }}
  />
);

const CopyButton = ({ text }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = (e) => {
    e.preventDefault();
    e.stopPropagation();
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <button
      onClick={handleCopy}
      title="Copy ID"
      style={{
        background: "transparent",
        border: "none",
        padding: "2px",
        cursor: "pointer",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        color: copied ? "#10b981" : "var(--ink-400)",
        borderRadius: "4px",
      }}
      onMouseEnter={(e) => {
        if (!copied) e.currentTarget.style.color = "var(--ink-700)";
      }}
      onMouseLeave={(e) => {
        if (!copied) e.currentTarget.style.color = "var(--ink-400)";
      }}
    >
      {copied ? <Check size={14} /> : <Copy size={14} />}
    </button>
  );
};

const HighlightedText = ({ text, searchTerm }) => {
  if (!searchTerm || !text) return text;

  const parts = text.split(new RegExp(`(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi'));

  return (
    <>
      {parts.map((part, i) =>
        part.toLowerCase() === searchTerm.toLowerCase() ? (
          <mark
            key={i}
            style={{
              background: "var(--saffron-light, #fcd34d)",
              padding: "1px 3px",
              borderRadius: "2px",
              fontWeight: 600,
              color: "inherit",
            }}
          >
            {part}
          </mark>
        ) : (
          <span key={i}>{part}</span>
        )
      )}
    </>
  );
};

export default function KMSReferences() {
  const [viewMode, setViewMode] = useState("card"); // "card" | "list"
  const [filters, setFilters] = useState({
    search: "",
    domain: "",
    validation_status: "",
    page: 1,
    page_size: 20,
  });
  const [data, setData] = useState({
    items: [],
    total: 0,
    page: 1,
    page_size: 20,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const params = { ...filters };
    if (!params.search) delete params.search;
    if (!params.domain) delete params.domain;
    if (!params.validation_status) delete params.validation_status;

    kmsApi
      .references(params)
      .then((d) => !cancelled && setData(d))
      .catch(
        (e) => !cancelled && setError(e?.response?.data?.error || e.message),
      )
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [filters]);

  const totalPages = Math.max(1, Math.ceil(data.total / data.page_size));

  return (
    <div style={{ width: "100%", paddingBottom: 64 }}>
      <style>{`
        @keyframes skeleton-loading {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
        .skeleton-box {
          background: linear-gradient(90deg, var(--ink-100, #f3f4f6) 25%, var(--ink-200, #e5e7eb) 50%, var(--ink-100, #f3f4f6) 75%);
          background-size: 200% 100%;
          animation: skeleton-loading 1.5s infinite linear;
        }
        .custom-select {
          appearance: none;
          -webkit-appearance: none;
          -moz-appearance: none;
          background: #ffffff;
          border: 1px solid var(--ink-200, #e2e8f0);
          border-radius: 8px;
          padding: 8px 36px 8px 14px;
          font-size: 14px;
          font-weight: 500;
          color: var(--ink-800, #1e293b);
          cursor: pointer;
          outline: none;
          transition: all 0.2s ease;
          width: 100%;
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
        }
        .custom-select:hover {
          border-color: var(--ink-300, #cbd5e1);
          background-color: var(--paper-1, #f8fafc);
        }
        .custom-select:focus {
          border-color: var(--saffron, #c26c1b);
          box-shadow: 0 0 0 3px rgba(194, 108, 27, 0.12);
        }
      `}</style>

      {/* Hero */}
      <div
        style={{ position: "relative", marginBottom: 32, padding: "16px 0" }}
      >
        <div
          className="devanagari-watermark"
          style={{
            position: "absolute",
            top: -30,
            right: 20,
            fontSize: 180,
            background:
              "linear-gradient(135deg, rgba(194,108,27,0.08) 0%, rgba(194,108,27,0.01) 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            pointerEvents: "none",
            userSelect: "none",
            fontWeight: 800,
          }}
        >
          ज्ञान
        </div>
        <div
          className="t-eyebrow"
          style={{ color: "var(--ink-500)", letterSpacing: "0.05em" }}
        >
          Sangathana Console / KMS Library
        </div>
        <h1
          className="t-display"
          style={{
            fontSize: 36,
            marginTop: 8,
            marginBottom: 12,
            color: "var(--ink-900)",
          }}
        >
          The corpus.
        </h1>
        <p
          style={{
            color: "var(--ink-600)",
            fontFamily: "var(--serif)",
            fontSize: 16,
            fontStyle: "italic",
            maxWidth: 720,
            lineHeight: 1.5,
          }}
        >
          {fmtNumber(data.total)} reference{data.total === 1 ? "" : "s"} indexed
          in the knowledge base.
        </p>
      </div>

      {/* Filters */}
      <div
        className="panel"
        style={{
          padding: "20px 24px",
          marginBottom: 24,
          background: "#ffffff",
          borderRadius: 16,
          border: "1px solid var(--ink-100)",
          boxShadow: "0 4px 20px -4px rgba(0, 0, 0, 0.02)",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-end",
            gap: 16,
            flexWrap: "wrap",
          }}
        >
          {/* Search bar (Width: 400px) */}
          <div style={{ width: 400 }}>
            <label
              className="t-eyebrow"
              style={{
                display: "block",
                marginBottom: 6,
                color: "var(--ink-500)",
              }}
            >
              Search
            </label>
            <div style={{ position: "relative" }}>
              <Search
                size={16}
                style={{
                  position: "absolute",
                  left: 12,
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "var(--ink-400)",
                }}
              />
              <input
                className="input"
                style={{ paddingLeft: 38, fontSize: 14, width: "100%" }}
                placeholder="Search by Title…"
                value={filters.search}
                onChange={(e) =>
                  setFilters((f) => ({ ...f, search: e.target.value, page: 1 }))
                }
              />
            </div>
          </div>

          {/* Right Aligned Controls (Domain dropdown + Validation + View Toggle) */}
          <div style={{ display: "flex", alignItems: "flex-end", gap: 16 }}>
            {/* Domain Dropdown */}
            <div style={{ minWidth: 170 }}>
              <label
                className="t-eyebrow"
                style={{
                  display: "block",
                  marginBottom: 6,
                  color: "var(--ink-500)",
                  fontSize: 11,
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                }}
              >
                Domain
              </label>
              <div style={{ position: "relative", display: "inline-block", width: "100%" }}>
                <select
                  className="custom-select"
                  value={filters.domain}
                  onChange={(e) =>
                    setFilters((f) => ({ ...f, domain: e.target.value, page: 1 }))
                  }
                >
                  {DOMAINS.map((d) => (
                    <option key={d} value={d}>
                      {d
                        ? d
                          .replace(/_/g, " ")
                          .replace(/\b\w/g, (char) => char.toUpperCase())
                        : "All domains"}
                    </option>
                  ))}
                </select>
                <ChevronDown
                  size={16}
                  style={{
                    position: "absolute",
                    right: 12,
                    top: "50%",
                    transform: "translateY(-50%)",
                    color: "var(--ink-400)",
                    pointerEvents: "none",
                  }}
                />
              </div>
            </div>

            {/* Status Dropdown */}
            <div style={{ minWidth: 170 }}>
              <label
                className="t-eyebrow"
                style={{
                  display: "block",
                  marginBottom: 6,
                  color: "var(--ink-500)",
                  fontSize: 11,
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                }}
              >
                Status
              </label>
              <div style={{ position: "relative", display: "inline-block", width: "100%" }}>
                <select
                  className="custom-select"
                  value={filters.validation_status}
                  onChange={(e) =>
                    setFilters((f) => ({
                      ...f,
                      validation_status: e.target.value,
                      page: 1,
                    }))
                  }
                >
                  <option value="">All statuses</option>
                  <option value="canonical">Canonical</option>
                  <option value="verified">Verified</option>
                  <option value="draft">Draft</option>
                  <option value="processed">Processed</option>
                </select>
                <ChevronDown
                  size={16}
                  style={{
                    position: "absolute",
                    right: 12,
                    top: "50%",
                    transform: "translateY(-50%)",
                    color: "var(--ink-400)",
                    pointerEvents: "none",
                  }}
                />
              </div>
            </div>

            {/* View Toggle */}
            <div>
              <div
                style={{
                  display: "inline-flex",
                  background: "var(--paper-2)",
                  border: "1px solid var(--ink-200)",
                  borderRadius: 8,
                  padding: 4,
                }}
              >
                <button
                  onClick={() => setViewMode("card")}
                  title="Card view"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "6px 12px",
                    border: "none",
                    background: viewMode === "card" ? "#ffffff" : "transparent",
                    color:
                      viewMode === "card" ? "var(--ink-900)" : "var(--ink-500)",
                    borderRadius: 6,
                    cursor: "pointer",
                    boxShadow:
                      viewMode === "card" ? "0 2px 4px rgba(0,0,0,0.05)" : "none",
                    transition: "all 0.2s",
                  }}
                >
                  <LayoutGrid size={16} />
                </button>
                <button
                  onClick={() => setViewMode("list")}
                  title="List view"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "6px 12px",
                    border: "none",
                    background: viewMode === "list" ? "#ffffff" : "transparent",
                    color:
                      viewMode === "list" ? "var(--ink-900)" : "var(--ink-500)",
                    borderRadius: 6,
                    cursor: "pointer",
                    boxShadow:
                      viewMode === "list" ? "0 2px 4px rgba(0,0,0,0.05)" : "none",
                    transition: "all 0.2s",
                  }}
                >
                  <List size={16} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div
          style={{
            padding: "16px 20px",
            borderRadius: 12,
            border: "1px solid var(--red)",
            background: "#fff1f2",
            color: "var(--red)",
            marginBottom: 24,
            display: "flex",
            alignItems: "center",
            gap: 12,
            fontWeight: 500,
          }}
        >
          {error}
        </div>
      )}

      {/* Loading States */}
      {loading && viewMode === "card" && (
        <div
          style={{
            display: "grid",
            gap: 16,
            gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
          }}
        >
          {[...Array(6)].map((_, i) => (
            <div
              key={i}
              className="panel"
              style={{
                padding: "20px 24px",
                background: "#ffffff",
                borderRadius: 16,
                border: "1px solid var(--ink-100)",
                boxShadow: "0 4px 20px -4px rgba(0, 0, 0, 0.02)",
                display: "flex",
                flexDirection: "column",
                gap: 12,
                minHeight: 180,
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <Skeleton width={80} height={18} borderRadius={4} />
                <Skeleton width={60} height={16} borderRadius={8} />
              </div>
              <div style={{ marginTop: 4 }}>
                <Skeleton
                  width="90%"
                  height={22}
                  style={{ marginBottom: 6, display: "block" }}
                />
                <Skeleton
                  width="60%"
                  height={22}
                  style={{ display: "block" }}
                />
              </div>
              <div style={{ marginTop: 4 }}>
                <Skeleton
                  width="100%"
                  height={14}
                  style={{ marginBottom: 4, display: "block" }}
                />
                <Skeleton
                  width="100%"
                  height={14}
                  style={{ marginBottom: 4, display: "block" }}
                />
                <Skeleton
                  width="80%"
                  height={14}
                  style={{ display: "block" }}
                />
              </div>
              <div
                style={{
                  display: "flex",
                  gap: 8,
                  marginTop: "auto",
                  paddingTop: 8,
                }}
              >
                <Skeleton width={50} height={16} borderRadius={8} />
                <Skeleton width={60} height={16} borderRadius={8} />
                <Skeleton
                  width={60}
                  height={16}
                  style={{ marginLeft: "auto" }}
                />
              </div>
            </div>
          ))}
        </div>
      )}

      {loading && viewMode === "list" && (
        <div
          className="panel"
          style={{
            background: "#ffffff",
            borderRadius: 16,
            border: "1px solid var(--ink-100)",
            boxShadow: "0 4px 20px -4px rgba(0, 0, 0, 0.02)",
            overflow: "hidden",
          }}
        >
          <table className="table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Title</th>
                <th>Domain</th>
                <th style={{ textAlign: "right" }}>References</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {[...Array(10)].map((_, i) => (
                <tr key={i}>
                  <td>
                    <Skeleton width={60} height={20} borderRadius={4} />
                  </td>
                  <td>
                    <Skeleton width="80%" height={20} borderRadius={4} />
                  </td>
                  <td>
                    <Skeleton width={70} height={24} borderRadius={12} />
                  </td>
                  <td style={{ textAlign: "right" }}>
                    <Skeleton width={40} height={20} />
                  </td>
                  <td>
                    <Skeleton width={60} height={24} borderRadius={12} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Empty State */}
      {!loading && !data.items.length && (
        <div
          style={{
            padding: "64px 24px",
            textAlign: "center",
            color: "var(--ink-400)",
            fontFamily: "var(--serif)",
            fontStyle: "italic",
            fontSize: 16,
            background: "var(--paper-2)",
            borderRadius: 16,
            border: "1px dashed var(--ink-200)",
          }}
        >
          No references match these filters.
        </div>
      )}

      {/* Data States */}
      {!loading && data.items.length > 0 && viewMode === "card" && (
        <div
          style={{
            display: "grid",
            gap: 16,
            gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
          }}
        >
          {data.items.map((r) => {
            const v =
              VALIDATION_BADGES[r.validation_status] || VALIDATION_BADGES.draft;
            return (
              <button
                key={r._id}
                onClick={() => setSelected(r)}
                className="panel"
                style={{
                  textAlign: "left",
                  padding: "20px 24px",
                  cursor: "pointer",
                  background: "#ffffff",
                  borderRadius: 16,
                  border: "1px solid var(--ink-100)",
                  boxShadow: "0 4px 20px -4px rgba(0, 0, 0, 0.02)",
                  display: "flex",
                  flexDirection: "column",
                  gap: 12,
                  transition: "all 0.2s cubic-bezier(0.34, 1.56, 0.64, 1)",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = "var(--saffron-light)";
                  e.currentTarget.style.transform = "translateY(-2px)";
                  e.currentTarget.style.boxShadow =
                    "0 12px 24px -8px rgba(194, 108, 27, 0.12)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = "var(--ink-100)";
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow =
                    "0 4px 20px -4px rgba(0, 0, 0, 0.02)";
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    gap: 8,
                  }}
                >
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 6 }}
                  >
                    <code
                      style={{
                        fontFamily: "var(--mono)",
                        fontSize: 11,
                        color: "var(--ink-500)",
                        background: "var(--ink-100)",
                        padding: "2px 6px",
                        borderRadius: 4,
                        wordBreak: "break-all",
                      }}
                    >
                      {r.external_id}
                    </code>
                    <CopyButton text={r.external_id} />
                  </div>
                  <span
                    className={`badge ${v.cls}`}
                    style={{
                      fontSize: 10,
                      padding: "3px 8px",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {v.label}
                  </span>
                </div>
                <div
                  style={{
                    fontFamily: "var(--serif)",
                    fontSize: 18,
                    fontWeight: 600,
                    lineHeight: 1.4,
                    color: "var(--ink-900)",
                    wordBreak: "break-word",
                  }}
                >
                  <HighlightedText text={r.title} searchTerm={filters.search} />
                </div>
                {r.content_excerpt && (
                  <div
                    style={{
                      fontSize: 14,
                      color: "var(--ink-600)",
                      lineHeight: 1.5,
                      display: "-webkit-box",
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {r.content_excerpt}
                  </div>
                )}
                <div
                  style={{
                    display: "flex",
                    gap: 8,
                    flexWrap: "wrap",
                    marginTop: "auto",
                    paddingTop: 8,
                  }}
                >
                  <span
                    className="badge badge-outline"
                    style={{ fontSize: 11, padding: "2px 8px" }}
                  >
                    {r.domain || "general"}
                  </span>
                  {r.reference_count > 0 && (
                    <span
                      style={{
                        fontSize: 12,
                        color: "var(--ink-500)",
                        marginLeft: "auto",
                        alignSelf: "center",
                        display: "flex",
                        alignItems: "center",
                        gap: 4,
                        fontWeight: 500,
                        whiteSpace: "nowrap",
                      }}
                    >
                      <Hash size={12} />
                      {fmtNumber(r.reference_count)} uses
                    </span>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      )}

      {!loading && data.items.length > 0 && viewMode === "list" && (
        <div
          className="panel"
          style={{
            background: "#ffffff",
            borderRadius: 16,
            border: "1px solid var(--ink-100)",
            boxShadow: "0 4px 20px -4px rgba(0, 0, 0, 0.02)",
            overflow: "hidden",
          }}
        >
          <table className="table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Title</th>
                <th>Domain</th>
                <th style={{ textAlign: "right" }}>References</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((r) => {
                const v =
                  VALIDATION_BADGES[r.validation_status] ||
                  VALIDATION_BADGES.draft;
                return (
                  <tr
                    key={r._id}
                    className="clickable"
                    onClick={() => setSelected(r)}
                  >
                    <td>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                        }}
                      >
                        <code
                          style={{
                            fontSize: 11,
                            color: "var(--ink-500)",
                            fontFamily: "var(--mono)",
                            background: "var(--ink-100)",
                            padding: "2px 6px",
                            borderRadius: 4,
                            wordBreak: "break-all",
                          }}
                        >
                          {r.external_id}
                        </code>
                        <CopyButton text={r.external_id} />
                      </div>
                    </td>
                    <td
                      style={{
                        fontWeight: 600,
                        color: "var(--ink-900)",
                        fontFamily: "var(--serif)",
                        wordBreak: "break-word",
                      }}
                    >
                      <HighlightedText text={r.title} searchTerm={filters.search} />
                    </td>
                    <td>
                      <span className="badge badge-outline">
                        {r.domain || "general"}
                      </span>
                    </td>
                    <td
                      style={{
                        textAlign: "right",
                        fontVariantNumeric: "tabular-nums",
                        fontWeight: 500,
                        color: "var(--ink-700)",
                      }}
                    >
                      {fmtNumber(r.reference_count)}
                    </td>
                    <td>
                      <span className={`badge ${v.cls}`}>{v.label}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginTop: 32,
          fontSize: 13,
          color: "var(--ink-600)",
          fontWeight: 500,
        }}
      >
        <span>
          Showing {data.items.length ? (data.page - 1) * data.page_size + 1 : 0}
          –{(data.page - 1) * data.page_size + data.items.length} of{" "}
          {fmtNumber(data.total)}
        </span>
        <div style={{ display: "flex", gap: 8 }}>
          <button
            className="btn"
            disabled={data.page <= 1}
            onClick={() => setFilters((f) => ({ ...f, page: f.page - 1 }))}
            style={{ padding: "8px 12px" }}
          >
            <ChevronLeft size={14} /> Prev
          </button>
          <div
            style={{
              padding: "8px 16px",
              alignSelf: "center",
              background: "var(--paper-2)",
              borderRadius: 8,
              fontFamily: "var(--mono)",
              fontSize: 13,
              color: "var(--ink-700)",
            }}
          >
            {data.page} / {totalPages}
          </div>
          <button
            className="btn"
            disabled={data.page >= totalPages}
            onClick={() => setFilters((f) => ({ ...f, page: f.page + 1 }))}
            style={{ padding: "8px 12px" }}
          >
            Next <ChevronRight size={14} />
          </button>
        </div>
      </div>

      {selected && (
        <ReferenceDetail item={selected} searchTerm={filters.search} onClose={() => setSelected(null)} />
      )}
    </div>
  );
}

function ReferenceDetail({ item, searchTerm, onClose }) {
  useBodyScrollLock(true);
  const v =
    VALIDATION_BADGES[item.validation_status] || VALIDATION_BADGES.draft;
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(44, 26, 14, 0.5)",
        backdropFilter: "blur(4px)",
        WebkitBackdropFilter: "blur(4px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 50,
        padding: "40px 20px",
      }}
      onClick={onClose}
    >
      <div
        className="panel"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 720,
          maxWidth: "100%",
          maxHeight: "90vh",
          overflowY: "auto",
          padding: "32px 40px",
          borderRadius: 20,
          background: "#ffffff",
          boxShadow: "0 24px 48px -12px rgba(0,0,0,0.2)",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            marginBottom: 20,
          }}
        >
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <code
                style={{
                  fontFamily: "var(--mono)",
                  fontSize: 12,
                  color: "var(--ink-500)",
                  background: "var(--ink-100)",
                  padding: "4px 8px",
                  borderRadius: 6,
                  wordBreak: "break-all",
                }}
              >
                {item.external_id}
              </code>
              <CopyButton text={item.external_id} />
            </div>
            <h2
              className="t-display"
              style={{
                fontSize: 32,
                marginTop: 12,
                marginBottom: 12,
                color: "var(--ink-900)",
                lineHeight: 1.3,
                wordBreak: "break-word",
              }}
            >
              <HighlightedText text={item.title} searchTerm={searchTerm} />
            </h2>
            <div
              style={{
                display: "flex",
                gap: 8,
                flexWrap: "wrap",
                alignItems: "center",
                fontSize: 13,
              }}
            >
              <span
                className={`badge ${v.cls}`}
                style={{ padding: "4px 10px" }}
              >
                {v.label}
              </span>
              <span
                className="badge badge-outline"
                style={{ padding: "4px 10px" }}
              >
                {item.domain || "general"}
              </span>
              {item.author && (
                <span
                  style={{
                    color: "var(--ink-500)",
                    fontStyle: "italic",
                    fontFamily: "var(--serif)",
                  }}
                >
                  by {item.author}
                </span>
              )}
            </div>
            <div
              style={{
                marginTop: 10,
                display: "flex",
                gap: 8,
                flexWrap: "wrap",
                fontSize: 13,
                color: "var(--ink-600)",
              }}
            >
            </div>
          </div>
          <button
            onClick={onClose}
            className="btn btn-ghost"
            style={{
              padding: 8,
              borderRadius: "50%",
              background: "var(--paper-2)",
              border: "none",
            }}
          >
            <X size={18} style={{ color: "var(--ink-600)" }} />
          </button>
        </div>

        {item.content_excerpt && (
          <div
            style={{
              padding: "24px 28px",
              background: "var(--paper-2)",
              borderRadius: 12,
              marginTop: 24,
              marginBottom: 24,
              fontFamily: "var(--serif)",
              fontSize: 16,
              lineHeight: 1.7,
              fontStyle: "italic",
              color: "var(--ink-900)",
              borderLeft: "4px solid var(--saffron)",
            }}
          >
            {item.content_excerpt}
          </div>
        )}

        <div
          style={{
            display: "grid",
            gap: 16,
            gridTemplateColumns: "1fr 1fr",
            marginBottom: 24,
            padding: "20px 24px",
            background: "#ffffff",
            border: "1px solid var(--ink-100)",
            borderRadius: 12,
          }}
        >
          <div>
            <div
              className="t-eyebrow"
              style={{
                marginBottom: 8,
                color: "var(--ink-500)",
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              <Hash size={14} /> References
            </div>
            <div
              style={{
                fontSize: 24,
                fontFamily: "var(--serif)",
                fontWeight: 600,
                color: "var(--ink-900)",
              }}
            >
              {fmtNumber(item.reference_count || 0)}
            </div>
          </div>
          <div>
            <div
              className="t-eyebrow"
              style={{
                marginBottom: 8,
                color: "var(--ink-500)",
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              <Calendar size={14} /> Last referenced
            </div>
            <div
              style={{ fontSize: 15, color: "var(--ink-800)", fontWeight: 500 }}
            >
              {item.last_referenced_at
                ? fmtRelative(item.last_referenced_at)
                : "Never yet"}
            </div>
          </div>
        </div>

        {item.tags?.length > 0 && (
          <div style={{ marginBottom: 24 }}>
            <div
              className="t-eyebrow"
              style={{
                marginBottom: 10,
                color: "var(--ink-500)",
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              <Tag size={14} /> Tags
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {item.tags.map((t) => (
                <span
                  key={t}
                  className="badge badge-neutral"
                  style={{ padding: "4px 10px", fontSize: 13 }}
                >
                  {t}
                </span>
              ))}
            </div>
          </div>
        )}

        <div
          style={{
            marginTop: 24,
            paddingTop: 24,
            borderTop: "1px solid var(--ink-100)",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div
            style={{
              fontSize: 12,
              color: "var(--ink-400)",
              fontFamily: "var(--mono)",
            }}
          >
            Indexed: {fmtDate(item.createdAt)}
          </div>
        </div>
      </div>
    </div>
  );
}